import React, { useEffect, useState } from 'react';
import { add_calendar_settings, get_all_calendar_settings } from '../api/calendarSettingsApi';
import { get_user_schedule, save_user_schedule } from '../../users/api/usersApi';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import { formatDateFlatpickr } from '../../../helper-functions/DateFunction';
import { permissionCheck } from '../../../helper-functions/PermissionCheck';
import { Button, Form, Row, Col } from 'react-bootstrap';
let calendar_setting_info_create, calendar_setting_info_view

const step = Number(process.env.REACT_APP_WEEKLY_HOURS_DIFFERENCE) || 15;

interface TimeSlot {
  from: string;
  to: string;
}

interface DayAvailability {
  day: string;
  enabled: boolean;
  slots: TimeSlot[];
}

// Define types for better type safety
type WeekSessionValue = 'full_day' | 'first_half' | 'second_half';

type WeekSessionsType = {
  [day: string]: {
    [week: number]: WeekSessionValue;
  };
};

// Define the options for statutory weekend days
const statutoryWeekendOptions: any = [
  { value: 'Sunday', label: 'Sunday' },
  { value: 'Monday', label: 'Monday' },
  { value: 'Tuesday', label: 'Tuesday' },
  { value: 'Wednesday', label: 'Wednesday' },
  { value: 'Thursday', label: 'Thursday' },
  { value: 'Friday', label: 'Friday' },
  { value: 'Saturday', label: 'Saturday' },
];

const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const daysOfWeekCapitalized = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

// Generate time interval options (12-hour format)
const generateTimeOptions = (): string[] => {
  const times: string[] = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += step) {
      const hour = h % 12 === 0 ? 12 : h % 12;
      const suffix = h < 12 ? "AM" : "PM";
      const minute = m.toString().padStart(2, "0");
      times.push(`${hour}:${minute} ${suffix}`);
    }
  }
  return times;
};

const timeOptions = generateTimeOptions();

function AddCalendarSettings() {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [weekStart, setWeekStart] = useState<string>('');
  const [workWeekStart, setWorkWeekStart] = useState<string>('');
  const [workWeekEnd, setWorkWeekEnd] = useState<string>('');
  const [halfWeekend, setHalfWeekend] = useState<boolean>(false);
  // State for validation errors
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  // State for week sessions (to store day-wise selections)
  const [weekSessions, setWeekSessions] = useState<WeekSessionsType>(
    daysOfWeek.reduce((acc, day) => {
      acc[day] = { 1: 'full_day', 2: 'full_day', 3: 'full_day', 4: 'full_day', 5: 'full_day' };
      return acc;
    }, {} as WeekSessionsType)
  );

  // State for whether individual day checkboxes are checked
  const [dayCheckboxes, setDayCheckboxes] = useState<{ [day: string]: { [week: number]: boolean } }>(
    daysOfWeek.reduce((acc, day) => {
      acc[day] = { 1: false, 2: false, 3: false, 4: false, 5: false };
      return acc;
    }, {} as { [day: string]: { [week: number]: boolean } })
  );
  const [yearStartMonth, setYearStartMonth] = useState<string>('');
  const [yearStartDay, setYearStartDay] = useState<string>('');
  const [yearEndMonth, setYearEndMonth] = useState<string>('');
  const [yearEndDay, setYearEndDay] = useState<string>('');
  
  // Weekly Availability state
  const [availability, setAvailability] = useState<DayAvailability[]>(
    daysOfWeekCapitalized.map((day) => ({
      day,
      enabled: false,
      slots: [{ from: "9:00 AM", to: "5:00 PM" }],
    }))
  );
  
  const { data: allCalendarSettingData } = useQuery('get_all_calendar_settings', get_all_calendar_settings, { refetchOnWindowFocus: false });
  
  // Fetch organization schedule (user_id: 0)
  useEffect(() => {
    const fetchSchedule = async () => {
      try {
        const res = await get_user_schedule(0);
        if (res?.data?.schedule) {
          const apiData = res.data.schedule;
          const updatedAvailability = daysOfWeekCapitalized.map((day) => {
            const key = day.toLowerCase();
            const data = apiData[key];
            if (data) {
              return {
                day,
                enabled: data.enabled,
                slots: data.slots
                  ? [{ from: data.slots.from, to: data.slots.to }]
                  : [{ from: "9:00 AM", to: "5:00 PM" }],
              };
            }
            return { day, enabled: false, slots: [{ from: "9:00 AM", to: "5:00 PM" }] };
          });
          setAvailability(updatedAvailability);
        }
      } catch (err) {
        console.error("Error fetching organization schedule:", err);
      }
    };
    fetchSchedule();
  }, []);
  
  useEffect(() => {
    if (allCalendarSettingData) {
      const calendarSettings = allCalendarSettingData.data;

      const weekStartsSetting = calendarSettings.find(setting => setting.setting_key === 'week_starts');
      const workWeekFromSetting = calendarSettings.find(setting => setting.setting_key === 'work_week_from');
      const workWeekToSetting = calendarSettings.find(setting => setting.setting_key === 'work_week_to');
      const yearStartMonthSetting = calendarSettings.find(setting => setting.setting_key === 'year_start_month');
      const yearStartDaySetting = calendarSettings.find(setting => setting.setting_key === 'year_start_day');
      const yearEndMonthSetting = calendarSettings.find(setting => setting.setting_key === 'year_end_month');
      const yearEndDaySetting = calendarSettings.find(setting => setting.setting_key === 'year_end_day');

      if (weekStartsSetting) setWeekStart(weekStartsSetting.setting_value);
      if (workWeekFromSetting) setWorkWeekStart(workWeekFromSetting.setting_value);
      if (workWeekToSetting) setWorkWeekEnd(workWeekToSetting.setting_value);
      if (yearStartMonthSetting) setYearStartMonth(String(yearStartMonthSetting.setting_value ?? '').padStart(2, '0'));
      if (yearStartDaySetting) setYearStartDay(String(yearStartDaySetting.setting_value ?? '').padStart(2, '0'));
      if (yearEndMonthSetting) setYearEndMonth(String(yearEndMonthSetting.setting_value ?? '').padStart(2, '0'));
      if (yearEndDaySetting) setYearEndDay(String(yearEndDaySetting.setting_value ?? '').padStart(2, '0'));
    }
  }, [allCalendarSettingData]);
  useEffect(() => {
    if (allCalendarSettingData) {
      const calendarSettings = allCalendarSettingData.data;
      const weekSessionsSetting = calendarSettings.find(setting => setting.week_sessions);

      let updatedWeekSessions: WeekSessionsType = {};
      let updatedDayCheckboxes: { [day: string]: { [week: number]: boolean } } = {};

      // Initialize empty objects for each day
      daysOfWeek.forEach((day) => {
        updatedWeekSessions[day] = {};
        updatedDayCheckboxes[day] = { 1: false, 2: false, 3: false, 4: false, 5: false }; // Default false
      });

      if (weekSessionsSetting && weekSessionsSetting.week_sessions) {
        // Loop through weeks (1-5)
        Object.keys(weekSessionsSetting.week_sessions).forEach((week) => {
          const weekData = weekSessionsSetting.week_sessions[week];

          Object.keys(weekData).forEach((day) => {
            if (!updatedWeekSessions[day]) {
              updatedWeekSessions[day] = {};
            }
            if (!updatedDayCheckboxes[day]) {
              updatedDayCheckboxes[day] = {};
            }

            const value = weekData[day]; // Can be "full_day", "first_half", "second_half", or null

            updatedWeekSessions[day][parseInt(week)] = value || "full_day"; // Default "full_day" if null

            // ✅ If value exists, check the checkbox; otherwise, default to false
            updatedDayCheckboxes[day][parseInt(week)] = value !== null;
          });
        });
      }
      setWeekSessions(updatedWeekSessions);
      setDayCheckboxes(updatedDayCheckboxes); // ✅ Update checkbox state
    }
  }, [allCalendarSettingData]);
  const handleWeekSessionChange = (day: string, week: number, value: WeekSessionValue) => {
    setWeekSessions(prevWeekSessions => ({
      ...prevWeekSessions,
      [day]: {
        ...prevWeekSessions[day],
        [week]: value,
      },
    }));
  };

  // Function to handle checkbox change for each day and week
  const handleCheckboxChange = (day: string, week: number, isChecked: boolean) => {
    setDayCheckboxes(prevDayCheckboxes => ({
      ...prevDayCheckboxes,
      [day]: {
        ...prevDayCheckboxes[day],
        [week]: isChecked,
      },
    }));
  };

  // Weekly Availability Handlers
  const handleToggleDay = (index: number) => {
    const updated = [...availability];
    updated[index].enabled = !updated[index].enabled;
    setAvailability(updated);
  };

  const handleTimeChange = (
    dayIndex: number,
    slotIndex: number,
    field: keyof TimeSlot,
    value: string
  ) => {
    const updated = [...availability];
    updated[dayIndex].slots[slotIndex][field] = value;
    setAvailability(updated);
  };

  const mutation = useMutation(add_calendar_settings);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const newErrors: { [key: string]: string } = {};
    if (!weekStart) {
      newErrors.weekStart = 'Week starts on is required.';
    }
    if (!workWeekStart) {
      newErrors.workWeekStart = 'Work week starts on is required.';
    }
    if (!workWeekEnd) {
      newErrors.workWeekEnd = 'Work week ends on is required.';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return; // Stop the submission if there are validation errors
    }

    setLoading(true);

    try {
      // Save calendar settings
      await mutation.mutateAsync({
        week_starts: weekStart,
        work_week_from: workWeekStart,
        work_week_to: workWeekEnd,
        year_start_month: yearStartMonth,
        year_start_day: yearStartDay,
        year_end_month: yearEndMonth,
        year_end_day: yearEndDay,
      });

      // Save weekly availability (organization schedule with user_id: 0)
      const schedulePayload: any = {
        user_id: 0,
      };

      availability.forEach((day) => {
        const key = day.day.toLowerCase();
        if (day.enabled) {
          schedulePayload[key] = {
            enabled: true,
            slots: {
              from: day.slots[0].from,
              to: day.slots[0].to,
            },
          };
        } else {
          schedulePayload[key] = {
            enabled: false,
            slots: "",
          };
        }
      });

      await save_user_schedule(schedulePayload);
      
      // Invalidate and refetch - smooth UX without page reload
      await queryClient.invalidateQueries('get_all_calendar_settings');
      
      toast.success("Calendar settings and weekly availability saved successfully!", {
        position: "top-right",
        autoClose: 2000,
      });
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to save settings", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className='card mb-5 mb-xl-10'>
      <div className='card-header border-0'>
        <div className='card-title m-0'>
          <h3 className='fw-bolder m-0'>Add Calendar Settings</h3>
        </div>
      </div>
      <div id='kt_account_profile_details' className='collapse show'>
        <form noValidate className='form' onSubmit={handleSubmit}>
          <div className='card-body border-top p-9'>
            {/* Week Definition */}
            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-bold fs-6'>Week starts on</label>
              <div className='col-lg-8 fv-row'>
                <select
                  className={`form-select form-select-solid form-select-lg fw-bold ${errors.weekStart ? 'is-invalid' : ''}`}
                  value={weekStart}
                  onChange={(e) => {
                    setWeekStart(e.target.value);
                    setErrors(prevErrors => ({ ...prevErrors, weekStart: '' })); // Clear error on change
                  }}
                >
                  <option value=''>Select Week starts on</option>
                  <option value='Sunday'>Sunday</option>
                  <option value='Monday'>Monday</option>
                  <option value='Tuesday'>Tuesday</option>
                  <option value='Wednesday'>Wednesday</option>
                  <option value='Thursday'>Thursday</option>
                  <option value='Friday'>Friday</option>
                  <option value='Saturday'>Saturday</option>
                </select>
                {errors.weekStart && (
                  <div className='fv-plugins-message-container invalid-feedback'>
                    <div data-field='weekStart'>{errors.weekStart}</div>
                  </div>
                )}
              </div>
            </div>
            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-bold fs-6'>Work week starts on</label>
              <div className='col-lg-8 fv-row'>
                <select
                  className={`form-select form-select-solid form-select-lg fw-bold ${errors.workWeekStart ? 'is-invalid' : ''}`}
                  value={workWeekStart}
                  onChange={(e) => {
                    setWorkWeekStart(e.target.value);
                    setErrors(prevErrors => ({ ...prevErrors, workWeekStart: '' })); // Clear error on change
                  }}
                >
                  <option value=''>Select Work week starts on</option>
                  <option value='Sunday'>Sunday</option>
                  <option value='Monday'>Monday</option>
                  <option value='Tuesday'>Tuesday</option>
                  <option value='Wednesday'>Wednesday</option>
                  <option value='Thursday'>Thursday</option>
                  <option value='Friday'>Friday</option>
                  <option value='Saturday'>Saturday</option>
                </select>
                {errors.workWeekStart && (
                  <div className='fv-plugins-message-container invalid-feedback'>
                    <div data-field='workWeekStart'>{errors.workWeekStart}</div>
                  </div>
                )}
              </div>
            </div>
            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-bold fs-6'>Work week ends on</label>
              <div className='col-lg-8 fv-row'>
                <select
                  className={`form-select form-select-solid form-select-lg fw-bold ${errors.workWeekEnd ? 'is-invalid' : ''}`}
                  value={workWeekEnd}
                  onChange={(e) => {
                    setWorkWeekEnd(e.target.value);
                    setErrors(prevErrors => ({ ...prevErrors, workWeekEnd: '' })); // Clear error on change
                  }}
                >
                  <option value=''>Select Work week ends on</option>
                  <option value='Sunday'>Sunday</option>
                  <option value='Monday'>Monday</option>
                  <option value='Tuesday'>Tuesday</option>
                  <option value='Wednesday'>Wednesday</option>
                  <option value='Thursday'>Thursday</option>
                  <option value='Friday'>Friday</option>
                  <option value='Saturday'>Saturday</option>
                </select>
                {errors.workWeekEnd && (
                  <div className='fv-plugins-message-container invalid-feedback'>
                    <div data-field='workWeekEnd'>{errors.workWeekEnd}</div>
                  </div>
                )}
              </div>
            </div>
            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-bold fs-6'>Year Start</label>
              <div className='col-lg-4 fv-row'>
                <select
                  className='form-select form-select-solid form-select-lg fw-bold'
                  value={yearStartDay}
                  onChange={(e) => setYearStartDay(e.target.value)}
                >
                  <option value=''>Day</option>
                  {Array.from({ length: 31 }, (_, i) => (
                    <option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                      {i + 1}
                    </option>
                  ))}
                </select>
              </div>
              <div className='col-lg-4 fv-row'>
                <select
                  className='form-select form-select-solid form-select-lg fw-bold'
                  value={yearStartMonth}
                  onChange={(e) => setYearStartMonth(e.target.value)}
                >
                  <option value=''>Month</option>
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                      {new Date(0, i).toLocaleString('default', { month: 'long' })}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-bold fs-6'>Year End</label>
              <div className='col-lg-4 fv-row'>
                <select
                  className='form-select form-select-solid form-select-lg fw-bold'
                  value={yearEndDay}
                  onChange={(e) => setYearEndDay(e.target.value)}
                >
                  <option value=''>Day</option>
                  {Array.from({ length: 31 }, (_, i) => (
                    <option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                      {i + 1}
                    </option>
                  ))}
                </select>
              </div>
              <div className='col-lg-4 fv-row'>
                <select
                  className='form-select form-select-solid form-select-lg fw-bold'
                  value={yearEndMonth}
                  onChange={(e) => setYearEndMonth(e.target.value)}
                >
                  <option value=''>Month</option>
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                      {new Date(0, i).toLocaleString('default', { month: 'long' })}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Weekly Availability Section */}
            <div className='row mb-6'>
              <div className='col-lg-12'>
                <h4 className='mb-4 fw-semibold text-gray-700'>Weekly Availability</h4>
                
                {availability.map((dayItem, dayIndex) => (
                  <div
                    key={dayItem.day}
                    className={`border rounded p-3 mb-3 ${dayItem.enabled ? "bg-light" : "bg-light-subtle opacity-75"}`}
                  >
                    <Row className='align-items-center mb-2'>
                      <Col xs='auto'>
                        <Form.Check
                          type='checkbox'
                          label={dayItem.day}
                          checked={dayItem.enabled}
                          onChange={() => handleToggleDay(dayIndex)}
                        />
                      </Col>
                    </Row>

                    {dayItem.enabled &&
                      dayItem.slots.map((slot, slotIndex) => (
                        <Row key={slotIndex} className='align-items-center mb-2 ms-4'>
                          <Col md={3}>
                            <Form.Select
                              value={slot.from}
                              onChange={(e) =>
                                handleTimeChange(dayIndex, slotIndex, "from", e.target.value)
                              }
                            >
                              {timeOptions.map((time) => (
                                <option key={time}>{time}</option>
                              ))}
                            </Form.Select>
                          </Col>

                          <Col md={3}>
                            <Form.Select
                              value={slot.to}
                              onChange={(e) =>
                                handleTimeChange(dayIndex, slotIndex, "to", e.target.value)
                              }
                            >
                              {timeOptions.map((time) => (
                                <option key={time}>{time}</option>
                              ))}
                            </Form.Select>
                          </Col>
                        </Row>
                      ))}
                  </div>
                ))}
              </div>
            </div>

            {/* Submit Button */}
            <div className='d-flex justify-content-end'>
              <button
                type='submit'
                className='btn btn-primary'
                disabled={loading}
              >
                {loading && (
                  <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true'></span>
                )}
                {loading ? 'Saving Settings...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddCalendarSettings;